import assert from 'assert';
import type { FragmentType } from 'src/gql';
import { Text } from '@savvy-kit/foundations/text';
import { isNil } from 'es-toolkit';
import TrackedButton from 'src/components/tracked/tracked-button';
import { graphql, unmaskFragment } from 'src/gql';
import { isSignedRequiredOnboardingTask } from 'src/utils/client-roadmap';

import { useAuth } from '@savvy/deprecated-shared/hooks/use-auth';

import GuidedFlowStepDoneIcon from '../../icons/custom/done';

const ClientOnboardingTaskStatusFragment = graphql(/* GraphQL */ `
  fragment ClientOnboardingTaskStatus on Task {
    user {
      isPrimaryClient
    }
    maybeDocusignSigned
    onboardingTask {
      coClientDocusignSignedAt
      coClientIaaSignedAt
      primaryClientDocusignSignedAt
      primaryClientIaaSignedAt
    }
    status
  }
`);

export const HouseholdOnboardingCallToActionFragment = graphql(/* GraphQL */ `
  fragment HouseholdOnboardingCallToAction on Task {
    user {
      id
      isPrimaryClient
      firstName
      lastName
    }
    type
    status
    onboardingTask {
      coClientDocusignSignedAt
      coClientIaaSignedAt
      primaryClientDocusignSignedAt
      primaryClientIaaSignedAt
    }
    maybeDocusignSigned
    ...ClientOnboardingTaskStatus
  }
`);

type Props = {
  readonly requriedOnboardingTaskFrags: FragmentType<typeof HouseholdOnboardingCallToActionFragment>[];
};
function HouseholdOnboardingCallToAction({ requriedOnboardingTaskFrags }: Props) {
  const { user: currUser } = useAuth();
  assert(!isNil(currUser), 'User is not authenticated');
  const householdOnboardingTasks = requriedOnboardingTaskFrags.map((taskFrag) =>
    unmaskFragment(HouseholdOnboardingCallToActionFragment, taskFrag),
  );
  const primaryClientOnboardingTask = householdOnboardingTasks.find((task) => task.user.isPrimaryClient);
  const coClientOnboardingTask = householdOnboardingTasks.find((task) => !task.user.isPrimaryClient);
  const primaryDidNotSign = !isSignedRequiredOnboardingTask({
    status: primaryClientOnboardingTask?.status,
    docusignSignedAt: primaryClientOnboardingTask?.onboardingTask?.primaryClientDocusignSignedAt,
    iaaSignedAt: primaryClientOnboardingTask?.onboardingTask?.primaryClientIaaSignedAt,
  });
  const coClientDidNotSign = !isSignedRequiredOnboardingTask({
    status: coClientOnboardingTask?.status,
    docusignSignedAt: coClientOnboardingTask?.onboardingTask?.coClientDocusignSignedAt,
    iaaSignedAt: coClientOnboardingTask?.onboardingTask?.coClientIaaSignedAt,
  });
  const showOnboardingTodoButton = currUser.isPrimaryClient ? primaryDidNotSign : coClientDidNotSign;

  return (
    <div>
      <div className="flex items-center justify-between gap-4 border-b border-light py-4">
        <Text variant="body-sm" render={<span />}>
          {primaryClientOnboardingTask?.user.firstName} {primaryClientOnboardingTask?.user.lastName}
        </Text>
        {!isNil(primaryClientOnboardingTask) && <ClientOnboardingTaskStatus taskFrag={primaryClientOnboardingTask} />}
      </div>
      <div className="flex items-center justify-between gap-4 py-4">
        <span>
          {coClientOnboardingTask?.user.firstName} {coClientOnboardingTask?.user.lastName}
        </span>
        {!isNil(coClientOnboardingTask) && <ClientOnboardingTaskStatus taskFrag={coClientOnboardingTask} />}
      </div>
      {showOnboardingTodoButton && (
        <div className="py-4 pr-4">
          <TrackedButton
            elementName="household-onboarding-call-to-action-button"
            onClick={() => (window.location.href = '/onboarding')}
            className="w-full"
          >
            Open
          </TrackedButton>
        </div>
      )}
    </div>
  );
}

type ClientOnboardingTaskStatusParams = {
  readonly taskFrag: FragmentType<typeof ClientOnboardingTaskStatusFragment>;
};
export function ClientOnboardingTaskStatus({ taskFrag }: ClientOnboardingTaskStatusParams) {
  const task = unmaskFragment(ClientOnboardingTaskStatusFragment, taskFrag);
  const isNotRequiredTask = task.status !== 'in_progress' && task.status !== 'todo';

  const docusignSignedAt = task.user.isPrimaryClient
    ? task.onboardingTask?.primaryClientDocusignSignedAt
    : task.onboardingTask?.coClientDocusignSignedAt;
  const iaaSignedAt = task.user.isPrimaryClient
    ? task.onboardingTask?.primaryClientIaaSignedAt
    : task.onboardingTask?.coClientIaaSignedAt;

  const hasSigned = isNotRequiredTask || (!isNil(docusignSignedAt) && !isNil(iaaSignedAt));

  return (
    <div className="flex flex-row gap-4 items-end justify-end">
      {hasSigned && <GuidedFlowStepDoneIcon />}
      <Text variant="body-sm" color="secondary" render={<span />}>
        {hasSigned ? 'Signed' : 'Needs to sign'}
      </Text>
    </div>
  );
}

export default HouseholdOnboardingCallToAction;
